import { SearchOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Checkbox,
  Flex,
  Input,
  Space,
  Table,
  Typography,
  theme,
} from "antd";
import type { IUkhoWorkRow } from "interfaces";
import React, { useState } from "react";
import { apiErrorMessage, useBackStillParked } from "./api";
import { handledChipText, UkhoDialogTarget } from "./lib";
import { ReactivateWithDateModal } from "./ReactivateWithDateModal";
import { useDebouncedValue } from "./useDebouncedValue";
import {
  buildWorkTableColumns,
  greyHandledRow,
  SecondaryLine,
  WrappingTag,
} from "./workTableColumns";

const EMPTY_TEXT =
  "Nothing waiting. Every ship that came back per UKHO is active again.";

interface BackStillParkedTabProps {
  /** Refreshes the live counts on the tab labels. */
  refetchCounts: () => void;
}

/**
 * Spec 11.2 "Back but still parked": ships whose latest AVCS-moving UKHO
 * notice is an arriving one and which eMariner still shows under Deactivated
 * Vessels. Oldest first (the API's order); "show handled" adds this year's
 * reactivated ones, greyed.
 */
export const BackStillParkedTab: React.FC<BackStillParkedTabProps> = ({
  refetchCounts,
}) => {
  const { token } = theme.useToken();
  const [searchText, setSearchText] = useState("");
  const [showHandled, setShowHandled] = useState(false);
  const [reactivateTarget, setReactivateTarget] =
    useState<UkhoDialogTarget | null>(null);
  const search = useDebouncedValue(searchText);
  const { data, isLoading, isFetching, isError, error, refetch } =
    useBackStillParked(showHandled, search);

  const handleReactivated = () => {
    setReactivateTarget(null);
    refetch();
    refetchCounts();
  };

  const columns = buildWorkTableColumns({
    dayColumnTitle: "Back per UKHO",
    today: new Date(),
    renderCustomer: (row) => (
      <>
        <div>{row.customer ?? ""}</div>
        {row.previousCustomer && (
          <SecondaryLine>{`was ${row.previousCustomer}`}</SecondaryLine>
        )}
      </>
    ),
    renderAction: (row: IUkhoWorkRow) =>
      row.handled ? (
        <WrappingTag color="green">
          {handledChipText("reactivated", row.handled)}
        </WrappingTag>
      ) : (
        <Button
          size="small"
          type="primary"
          onClick={() => setReactivateTarget(row)}
        >
          Reactivate with this date
        </Button>
      ),
  });

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Typography.Text type="secondary">
        UKHO moved the charts back to us; eMariner still shows the ship parked
      </Typography.Text>
      <Flex justify="space-between" align="center" gap="middle" wrap>
        <Input
          placeholder="Search ship, IMO or customer"
          prefix={<SearchOutlined />}
          allowClear
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          style={{ width: 260 }}
        />
        <Checkbox
          checked={showHandled}
          onChange={(event) => setShowHandled(event.target.checked)}
        >
          show handled
        </Checkbox>
      </Flex>
      {isError ? (
        <Alert type="error" showIcon message={apiErrorMessage(error)} />
      ) : (
        <Table<IUkhoWorkRow>
          rowKey={(row) => `${row.vesselId}-${row.transferId}`}
          columns={columns}
          dataSource={data}
          loading={isLoading || isFetching}
          onRow={(row) => greyHandledRow(row, token.colorTextTertiary)}
          locale={search.trim() ? undefined : { emptyText: EMPTY_TEXT }}
          pagination={{ defaultPageSize: 50, hideOnSinglePage: true }}
        />
      )}
      {reactivateTarget && (
        <ReactivateWithDateModal
          open
          target={reactivateTarget}
          onClose={() => setReactivateTarget(null)}
          onDone={handleReactivated}
        />
      )}
    </Space>
  );
};
